import React, { useState, useEffect } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import {
  ChevronDown,
  Shield,
  FileText,
  Fingerprint,
  UserCheck,
  Globe,
  TrendingUp,
  BookOpen,
  HelpCircle,
  Terminal,
  Play,
  ArrowRight,
  X,
  Sun,
  Moon,
  Menu,
  Briefcase,
  Layers,
  Database,
  ExternalLink
} from "lucide-react";
import Logo from "../assets/images/Logo.png";

const NavBar = () => {
  const [click, setClick] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const navigate = useNavigate();

  const handleClick = () => setClick(!click);

  useEffect(() => {
    document.body.className = "light";
    localStorage.setItem("darkMode", "false");
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const dropdowns = {
    solutions: [
      { title: "NIN Verification", desc: "Instantly check and verify National Identification Numbers", icon: Shield, path: "/signup" },
      { title: "BVN Verification", desc: "Secure Bank Verification Number details checks", icon: Fingerprint, path: "/signup" },
      { title: "CAC Verification", desc: "Corporate Registry verification for corporate onboarding", icon: FileText, path: "/signup" },
      { title: "Biometric Liveness", desc: "Advanced facial recognition & liveness check", icon: UserCheck, path: "/signup" }
    ],
    useCases: [
      { title: "Financial Services", desc: "Know Your Customer (KYC) compliance", icon: TrendingUp },
      { title: "E-Commerce Trust", desc: "Reduce fraud & chargebacks during transactions", icon: Globe },
      { title: "Remote Onboarding", desc: "Smooth user onboarding without boundaries", icon: Layers }
    ],
    resources: [
      { title: "Fraud Report 2026", desc: "Read latest trends in document and identity fraud", icon: TrendingUp },
      { title: "Help Center", desc: "Frequently asked questions & documentation", icon: HelpCircle },
      { title: "Developer Sandbox", desc: "Test Fylder API integrations easily", icon: Database }
    ],
    developers: [
      { title: "API Reference", desc: "Complete documentation for backend integration", icon: Terminal },
      { title: "Quickstart Guides", desc: "Integrate Fylder in under 10 minutes", icon: BookOpen }
    ],
    company: [
      { title: "About Us", desc: "Our mission to enable secure identity verification", icon: Briefcase },
      { title: "Contact Support", desc: "Reach out to our round-the-clock support desk", icon: HelpCircle, path: "/contact" }
    ]
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 w-full z-40 transition-all duration-300 ${scrolled
        ? "bg-white/90 dark:bg-gray-900/90 backdrop-blur-md shadow-sm border-b border-slate-200/50 dark:border-gray-800/50"
        : "bg-transparent"
        }`}>
        {/* Main Navbar */}
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <Link to="/" className="flex items-center gap-3">
              <img src={Logo} alt="Fylder Logo" className="h-20 w-auto object-contain dark:brightness-110" />
              <span className="text-2xl font-bold text-slate-800 dark:text-slate-200 tracking-tight">Fylder</span>
            </Link>
          </div>

          {/* Right Action buttons */}
          <div className="hidden lg:flex items-center gap-6">
            <a
              href="/#services"
              className="text-sm font-semibold text-slate-700 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
            >
              Services
            </a>
            <a
              href="https://wa.me/2349077968959" target="_blank" rel="noopener noreferrer"
              className="text-sm font-semibold flex items-center gap-1 text-slate-700 dark:text-slate-300 hover:text-green-600 transition-colors"
            >
              Live Support <ExternalLink size={14} />
            </a>
            <Link
              to="/contact"
              className="text-sm font-semibold text-slate-700 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
            >
              Contact Us
            </Link>
            <Link
              to="/login"
              className="text-sm font-semibold text-slate-700 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
            >
              Log in
            </Link>
            <Link
              to="/signup"
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-bold shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all"
            >
              Sign up
            </Link>
          </div>

          {/* Mobile Hamburg */}
          <div className="flex lg:hidden items-center gap-3">
            <button
              onClick={handleClick}
              className="p-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-gray-800 rounded-lg transition-colors cursor-pointer"
            >
              {click ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Dropdown Menu */}
        <div className={`lg:hidden w-full bg-white dark:bg-gray-900 border-t border-slate-200 dark:border-gray-850 overflow-hidden transition-all duration-300 ease-in-out ${click ? "max-h-[85vh] opacity-100" : "max-h-0 opacity-0 pointer-events-none"
          }`}>
          <div className="px-6 py-4 flex flex-col gap-5 text-sm font-semibold max-h-[80vh] overflow-y-auto">
            <div className="flex flex-col gap-4 pt-2">
              <a href="/#services" onClick={() => setClick(false)} className="text-slate-700 dark:text-slate-300 py-2 border-b border-slate-100 dark:border-gray-800">Services</a>
              <a href="https://wa.me/2349077968959" target="_blank" rel="noopener noreferrer" onClick={() => setClick(false)} className="flex items-center gap-1 text-slate-700 dark:text-slate-300 py-2 border-b border-slate-100 dark:border-gray-800 hover:text-green-600">
                Live Support <ExternalLink size={14} />
              </a>
              <Link to="/contact" onClick={() => setClick(false)} className="text-slate-700 dark:text-slate-300 py-2 border-b border-slate-100 dark:border-gray-800">Contact Us</Link>
              <Link to="/login" onClick={() => setClick(false)} className="text-slate-700 dark:text-slate-300 py-2 border-b border-slate-100 dark:border-gray-800">Log in</Link>
              <Link
                to="/signup"
                onClick={() => setClick(false)}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-center font-bold mt-2 shadow-md transition-colors"
              >
                Sign up
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Interactive Video Demo Modal */}
      {showDemoModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-4xl bg-gray-900 rounded-2xl overflow-hidden shadow-2xl border border-gray-800">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-800">
              <h3 className="text-sm font-bold text-white tracking-wide uppercase">Fylder Platform Tour</h3>
              <button
                onClick={() => setShowDemoModal(false)}
                className="p-1 rounded-full text-slate-400 hover:text-white hover:bg-gray-800 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Video Placeholder Content */}
            <div className="relative aspect-video bg-black flex flex-col justify-center items-center p-8 text-center">
              <div className="absolute inset-0 bg-gradient-to-tr from-indigo-950/40 via-transparent to-emerald-950/40 opacity-80 pointer-events-none" />

              {/* Play symbol with neon pulse */}
              <div className="relative h-20 w-20 rounded-full bg-blue-600/10 border border-blue-500/40 flex items-center justify-center text-blue-500 animate-pulse mb-6">
                <Play size={32} fill="currentColor" className="ml-1" />
                <div className="absolute inset-0 h-20 w-20 rounded-full border border-blue-400/20 animate-ping" />
              </div>

              <h4 className="text-xl md:text-2xl font-bold text-white max-w-lg z-10 leading-tight">
                Securely verifying real-world identities in 10 seconds or less
              </h4>
              <p className="text-sm text-slate-400 max-w-md mt-3 leading-relaxed z-10">
                This demo showcases automated NIN/BVN cross-matching, liveness anti-fraud filters, and biometric verification workflows.
              </p>

              <button
                onClick={() => { setShowDemoModal(false); navigate("/signup"); }}
                className="mt-6 flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-bold shadow-lg transition-transform hover:scale-105 active:scale-95"
              >
                Start Free Trial <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NavBar;
